import type {
  FilterNode,
  PredicateNode,
  RangeNode,
  AndNode,
  OrNode,
  NotNode,
} from "./nodes";
import { format } from "./format";
import { type Formatter } from "./Formatter";

/**
 * Condition formatter for use in Cesium3dTileStyle.
 * Outputs expressions like: (${a} === "x" || ${a} === "y") && (${b} === "l")
 */
export class Cesium3dTileStyleConditionFormatter implements Formatter {
  formatPredicate(node: PredicateNode): string {
    const { field, operator, value } = node;

    //standard comparison operators
    return `\${${field}} ${operator} ${this.formatValue(value)}`;
  }

  formatRange(node: RangeNode): string {
    const { field, gt, gte, lt, lte } = node;
    const conditions: string[] = [];

    if (gt !== undefined)
      conditions.push(`\${${field}} > ${this.formatValue(gt)}`);
    if (gte !== undefined)
      conditions.push(`\${${field}} >= ${this.formatValue(gte)}`);
    if (lt !== undefined)
      conditions.push(`\${${field}} < ${this.formatValue(lt)}`);
    if (lte !== undefined)
      conditions.push(`\${${field}} <= ${this.formatValue(lte)}`);

    if (conditions.length === 0) return "true";
    if (conditions.length === 1) return conditions[0];
    return `(${conditions.join(" && ")})`;
  }

  formatAnd(node: AndNode, format: (n: FilterNode) => string): string {
    if (node.children.length === 0) return "true";
    if (node.children.length === 1) return format(node.children[0]);

    const formatted = node.children.map((child) => {
      const childStr = format(child);
      //add parentheses for OR nodes to ensure correct precedence
      if (child.type === "or") {
        return `(${childStr})`;
      }
      return childStr;
    });

    return formatted.join(" && ");
  }

  formatOr(node: OrNode, format: (n: FilterNode) => string): string {
    if (node.children.length === 0) return "false";
    if (node.children.length === 1) return format(node.children[0]);

    const formatted = node.children.map(format);
    return formatted.join(" || ");
  }

  formatNot(node: NotNode, format: (n: FilterNode) => string): string {
    const childStr = format(node.child);
    return `!(${childStr})`;
  }

  private formatValue(
    value: string | number | boolean | string[] | number[],
  ): string {
    if (typeof value === "string") {
      return `"${value.replace(/"/g, '\\"')}"`; //escape quotes
    }
    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    if (Array.isArray(value)) {
      return `[${value.map((v) => this.formatValue(v)).join(", ")}]`;
    }
    return String(value);
  }
}

export function toCesium3dTileStyleCondition(node: FilterNode): string {
  return format(node, new Cesium3dTileStyleConditionFormatter());
}
